import FileUploadIcon from '@mui/icons-material/FileUpload';
import { Alert, Box, Button, Snackbar } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { Title, useRedirect } from 'react-admin';
import { IClinic } from '../../interfaces/clinic';
import {
  DocumentListFilters,
  DocumentListItem,
  DocumentListParams,
  DocumentReviewState,
} from '../../interfaces/document';
import { getClinicsForSelect } from '../../lib/clinic';
import referralService from '../../services/referral';
import { PageHeader } from '../common/PageHeader/PageHeader';
import '../common/tab-section.css';
import './document.css';
import DocumentActionDialog from './DocumentActionDialog';
import DocumentsFilters from './DocumentsFilters';
import DocumentsList from './DocumentsList';
import { useDocumentActions } from './hooks/useDocumentActions';
import { useDocumentFilters } from './hooks/useDocumentFilters';

const Documents: React.FC = () => {
  const [documents, setDocuments] = useState<DocumentListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [paginationMeta, setPaginationMeta] = useState({
    total: 0,
    totalPages: 0,
  });
  const [clinics, setClinics] = useState<Partial<IClinic>[]>([]);
  const redirect = useRedirect();

  // Use the custom hook for URL-synced filters
  const {
    filters,
    searchDocumentId,
    searchCrmId,
    searchPatientName,
    pagination,
    handleFilterChange,
    handleSearchChange,
    resetToPage1,
    setPagination,
  } = useDocumentFilters();

  /**
   * `overrides` exists for the quick views: `handleFilterChange` schedules a state update, so
   * a fetch fired in the same tick would still read the previous filters and the list would
   * lag one click behind the button.
   */
  const fetchDocuments = useCallback(
    async (overrides?: Partial<DocumentListFilters> & { page?: number }) => {
    setLoading(true);
    try {
      const params: DocumentListParams = {
        // `page` is overridable for the same reason the filters are: `resetToPage1()` is a
        // state update that has not landed by the time this runs, so from page 2 onward a
        // filter change would query the old page against the new filter.
        page: pagination.page,
        limit: pagination.limit,
        searchDocumentId: searchDocumentId || undefined,
        searchCrmId: searchCrmId || undefined,
        searchPatientName: searchPatientName || undefined,
        ...filters,
        ...overrides,
      };

      const response = await referralService.fetchDocuments(params);

      if (response) {
        setDocuments(response.documents);
        setPaginationMeta({
          total: response.pagination.total,
          totalPages: response.pagination.totalPages,
        });
      }
    } catch (error) {
      console.error('Error fetching documents:', error);
    } finally {
      setLoading(false);
    }
    },
    [
      pagination.page,
      pagination.limit,
      filters,
      searchDocumentId,
      searchCrmId,
      searchPatientName,
    ],
  );

  const {
    dialogState,
    snackbar,
    handleRetryProcessing,
    handleMarkResolved,
    handleDialogClose,
    handleReasonChange,
    handleConfirmAction,
    handleSnackbarClose,
  } = useDocumentActions(fetchDocuments);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  useEffect(() => {
    const getClinics = async () => {
      try {
        const clinicsAllowed = await getClinicsForSelect();
        const clinicsForSelect = clinicsAllowed.map((clinic) => ({
          id: clinic.id,
          name: clinic.name,
        }));
        setClinics(clinicsForSelect);
      } catch (error) {
        console.error('Error fetching clinics:', error);
      }
    };
    getClinics();
  }, []);

  const handleApplyFilters = () => {
    resetToPage1();
    fetchDocuments({ page: 1 });
  };

  /**
   * Applies a quick view in one action: set the review filter, go back to page 1, refetch.
   *
   * `fetchDocuments` reads the filters from the hook's state, which has not been committed
   * yet at this point, so the states are passed through explicitly rather than relied upon.
   */
  const handleApplyQuickView = (reviewStates?: DocumentReviewState[]) => {
    handleFilterChange('reviewStates', reviewStates);
    resetToPage1();
    fetchDocuments({ reviewStates, page: 1 });
  };

  const handlePageChange = (page: number) => {
    setPagination({ ...pagination, page });
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPagination({
      page: 1,
      limit: parseInt(event.target.value, 10),
    });
  };

  return (
    <Box>
      <Title title='Documents Library' />

      <Box className='tab-section'>
        <PageHeader
          breadcrumbs={[{ label: 'Documents' }, { label: 'Library', active: true }]}
          actions={
            <Button
              id='documents-upload-button'
              className='primary-button'
              variant='contained'
              startIcon={<FileUploadIcon />}
              onClick={() => redirect(`/documents/upload`)}>
              Upload Document
            </Button>
          }
        />

        <DocumentsFilters
          filters={filters}
          searchDocumentId={searchDocumentId}
          searchCrmId={searchCrmId}
          searchPatientName={searchPatientName}
          clinics={clinics}
          onFilterChange={handleFilterChange}
          onApplyQuickView={handleApplyQuickView}
          onSearchChange={handleSearchChange}
          onApplyFilters={handleApplyFilters}
        />

        <DocumentsList
          documents={documents}
          loading={loading}
          pagination={{
            page: pagination.page,
            limit: pagination.limit,
            total: paginationMeta.total,
            totalPages: paginationMeta.totalPages,
          }}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          onRetryProcessing={handleRetryProcessing}
          onMarkResolved={handleMarkResolved}
        />

        <DocumentActionDialog
          open={dialogState.open}
          action={dialogState.action}
          documentId={dialogState.documentId}
          reason={dialogState.reason}
          loading={dialogState.loading}
          onClose={handleDialogClose}
          onReasonChange={handleReasonChange}
          onConfirm={handleConfirmAction}
        />

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
          <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Box>
  );
};

export default Documents;
